/* eslint-disable @typescript-eslint/no-explicit-any */
import { find, isEmpty, map, isUndefined, first } from 'lodash';
import moment from 'moment';
import { IShopifyPromoCodeFormOptions, IShopifyPromoCodeFormValues, PrefixTypeStrategy, ProductCollections, PROMO_OFFER_TYPES, PurchaseType, RecurringCycleLimit } from '../components';
import { BOOST_TYPE, BoostEligibilitySelectionType, BoostInput, OFFER_PAYOUT_TYPE_PROMO, OFFER_PROMO_PREFIX_TYPE, OFFER_SOURCE, UpdatePromoInput } from '../types/globalTypes';
import { GetOfferById_offer_boosts_discountOffer_promos } from '../queries/types/GetOfferById';
import { TOfferMember, IAffiliateLinkMember, IPromoCodeMember, OfferMemberStatus, MemberStatus } from '../components/MemberTable/types';
import { GetOfferById } from '../queries/types/GetOfferById';
import { AFFILIATE_OFFER_LINK_STATUS } from '../constants/offers';
import { OFFER_PROMO_CODE_STATUS, AFFILIATE_STATUS } from '../types/globalTypes';
import { GetofferByIdWithStats_offer_offer_boosts } from '../queries/types/GetofferByIdWithStats';

export function transformDiscountBoostInputFromApi(apiDiscountBoost: GetOfferById_offer_boosts_discountOffer_promos): UpdatePromoInput {
  return {
    codeSuffix: apiDiscountBoost.codeSuffix,
    codePrefix: apiDiscountBoost.codePrefix,
    flatPayout: apiDiscountBoost.flatPayout,
    percentPayout: apiDiscountBoost.percentPayout,
    payoutType: apiDiscountBoost.payoutType,
    payouts: [],
    name: apiDiscountBoost.name,
    source: apiDiscountBoost.source,
    specialLimitOnePerSale: apiDiscountBoost.specialLimitOnePerSale,
    specialLimitUsageCapAmount: apiDiscountBoost.specialLimitUsageCapAmount,
    specialLimitUsageCapEnabled: apiDiscountBoost.specialLimitUsageCapEnabled,
    status: apiDiscountBoost.status,
    multiShopifyEnabled: apiDiscountBoost.multiShopifyEnabled,
    isSecureCodes: apiDiscountBoost.isSecureCodes,
    connectedClientMetadata: apiDiscountBoost.connectedClientMetadata,
    isSubscription: apiDiscountBoost.isSubscription,
    isOneTime: apiDiscountBoost.isOneTime,
    recurringCycleLimit: apiDiscountBoost.recurringCycleLimit,
    priceRuleAmount: apiDiscountBoost.priceRuleAmount,
    priceRuleType: apiDiscountBoost.priceRuleType,
    usageLimitAmount: apiDiscountBoost.usageLimitAmount,
    usageLimitRule: apiDiscountBoost.usageLimitRule,
    productCollections: apiDiscountBoost.providerMetadata?.productCollections,
    startDate: apiDiscountBoost.startDate ? moment(apiDiscountBoost.startDate) : null,
    endDate: apiDiscountBoost.endDate ? moment(apiDiscountBoost.endDate) : null,
    clientsForSync: [],
    discountCombination: {
      productDiscounts: false,
      orderDiscounts: false,
      shippingDiscounts: false,
    },
  };
}
export function transformBoostFromApi(apiBoost: GetofferByIdWithStats_offer_offer_boosts): BoostInput {
  const transformed = {
    activeEndDate: apiBoost.activeEndDate ? new Date(apiBoost.activeEndDate) : null,
    activeStartDate: apiBoost.activeStartDate ? new Date(apiBoost.activeStartDate) : null,
    boostType: apiBoost.boostType,
    label: apiBoost.label,
    maxLimit: apiBoost.maxLimit,
    triggerType: apiBoost.triggerType,
    triggerValue: apiBoost.triggerValue,
    id: apiBoost.id,
    hideInCreatorPortal: apiBoost.hideInCreatorPortal ?? undefined,
    requireApproval: apiBoost.requireApproval ?? undefined,
    boostEligibility: (apiBoost as any).boostEligibility ? {
      eligibilityType: (apiBoost as any).boostEligibility.eligibilityType,
      memberIds: (apiBoost as any).boostEligibility.memberIds,
      id: Number((apiBoost as any).boostEligibility.id) ?? null,
      advancedSelectionOperator: (apiBoost as any).boostEligibility.advancedSelectionOperator,
      advancedSelectionMetadata: (apiBoost as any).boostEligibility.advancedSelectionMetadata,
    } : null,
    discountBoostInput:
      apiBoost.boostType === BOOST_TYPE.DISCOUNT_BOOST && apiBoost.discountOffer?.promos?.length
        ? transformDiscountBoostInputFromApi(apiBoost.discountOffer.promos[0])
        : null,
    boostMetadata: apiBoost.boostMetadata,
    cashbackAmount: apiBoost.cashbackAmount,
  };

  return transformed;
}
export const processPromoCodeInput = (values: Partial<IShopifyPromoCodeFormValues>, allSubscriptionEligible: boolean) => {
    let flatPayout = null;
    let percentPayout = null;
    switch (values.payoutType) {
      case OFFER_PAYOUT_TYPE_PROMO.CONVERSION:
        flatPayout = Number(values.flatPayout);
        break;
      case OFFER_PAYOUT_TYPE_PROMO.SALE:
        percentPayout = Number(values.percentPayout);
        break;
      case OFFER_PAYOUT_TYPE_PROMO.CONVERSION_AND_SALE:
        flatPayout = Number(values.flatPayout);
        percentPayout = Number(values.percentPayout);
        break;
    }
    let prefixType = OFFER_PROMO_PREFIX_TYPE.FULL_NAME;
    let codeSuffix = '';
    switch (values.prefixType) {
      case PrefixTypeStrategy.FULL_NAME_ONLY:
        break;
      case PrefixTypeStrategy.FULL_NAME_PLUS_SUFFIX:
        codeSuffix = values.codeSuffix;
        break;
      case PrefixTypeStrategy.INSTAGRAM_PLUS_SUFFIX:
        prefixType = OFFER_PROMO_PREFIX_TYPE.IG_USERNAME;
        codeSuffix = values.codeSuffix;
        break;
      case PrefixTypeStrategy.INSTAGRAM_ONLY:
        prefixType = OFFER_PROMO_PREFIX_TYPE.IG_USERNAME;
        break;
      case PrefixTypeStrategy.FIRST_INITIAL_LAST_NAME_PLUS_SUFFIX:
        prefixType = OFFER_PROMO_PREFIX_TYPE.FIRST_INITIAL_LAST_NAME;
        codeSuffix = values.codeSuffix;
        break;
      case PrefixTypeStrategy.FIRST_INITIAL_LAST_NAME_ONLY:
        prefixType = OFFER_PROMO_PREFIX_TYPE.FIRST_INITIAL_LAST_NAME;
        break;
      case PrefixTypeStrategy.FIRST_NAME_LAST_INITIAL_PLUS_SUFFIX:
        prefixType = OFFER_PROMO_PREFIX_TYPE.FIRST_NAME_LAST_INITIAL;
        codeSuffix = values.codeSuffix;
        break;
      case PrefixTypeStrategy.FIRST_NAME_LAST_INITIAL_ONLY:
        prefixType = OFFER_PROMO_PREFIX_TYPE.FIRST_NAME_LAST_INITIAL;
        break;
    }
    let recurringCycleLimitAmount;
    switch (values.offerCodePurchaseRestrictionsRule) {
      case RecurringCycleLimit.LIMIT_DISCOUNT_TO_THE_FIRST_PAYMENT:
        recurringCycleLimitAmount = 1;
        break;
      case RecurringCycleLimit.LIMIT_DISCOUNT_TO_MULTIPLE_RECURRING_PAYMENTS:
        recurringCycleLimitAmount = values.recurringCycleLimitAmount.toString();
        break;
      case RecurringCycleLimit.DISCOUNT_APPLIES_TO_ALL_RECURRING_PAYMENTS:
        recurringCycleLimitAmount = 0;
        break;
      default:
        recurringCycleLimitAmount = 0;
        break;
    }

    let isOneTime = false;
    let isSubscription = false;
    if (allSubscriptionEligible) {
      switch (values.purchaseType) {
        case PurchaseType.ONE_TIME:
          isOneTime = true;
          isSubscription = false;
          break;
        case PurchaseType.SUBSCRIPTION:
          isSubscription = true;
          isOneTime = false;
          break;
        case PurchaseType.BOTH:
          isOneTime = true;
          isSubscription = true;
          break;
        default:
          break;
      }
    }
    return {
        flatPayout,
        percentPayout,
        prefixType,
        codeSuffix,
        recurringCycleLimitAmount,
        isOneTime,
        isSubscription,
      };
};

export const transformDiscountBoostInputToPromoCodeValuesForBoost = (
  discountInput: UpdatePromoInput,
): Partial<IShopifyPromoCodeFormValues> => {
  let purchaseType: PurchaseType | undefined;
  if (discountInput.isOneTime && discountInput.isSubscription) purchaseType = PurchaseType.BOTH;
  else if (discountInput.isOneTime) purchaseType = PurchaseType.ONE_TIME;
  else if (discountInput.isSubscription) purchaseType = PurchaseType.SUBSCRIPTION;

  let recurringCycleLimitAmount;
  switch (discountInput.recurringCycleLimit) {
    case 1:
      recurringCycleLimitAmount = 1;
      break;
    case 0:
      recurringCycleLimitAmount = 0;
      break;
    default:
      recurringCycleLimitAmount = discountInput.recurringCycleLimit.toString();
      break;
  }

  return {
    codeSuffix: discountInput.codeSuffix,
    description: '',
    flatPayout: null,
    groupName: discountInput.name,
    imageUrl: null,
    icon: null,
    name: discountInput.name,
    payoutType: null,
    payoutOptions: null,
    percentPayout: null,
    prefixType: PrefixTypeStrategy.FIRST_INITIAL_LAST_NAME_ONLY,
    priceRuleAmount: discountInput.priceRuleAmount != null
      ? discountInput.priceRuleAmount.toString()
      : '',
    priceRuleType: discountInput.priceRuleType,
    source: OFFER_SOURCE.SHOPIFY,
    specialLimitNewCustomersOnly: discountInput.specialLimitNewCustomersOnly,
    specialLimitOnePerSale: discountInput.specialLimitOnePerSale,
    specialLimitUsageCapAmount: discountInput.specialLimitUsageCapAmount?.toString(),
    specialLimitUsageCapEnabled: discountInput.specialLimitUsageCapEnabled,
    usageLimitAmount: discountInput.usageLimitAmount?.toString(),
    usageLimitRule: discountInput.usageLimitRule,
    productCollections: map(discountInput.productCollections, (collection) => collection.id),
    status: discountInput.status,
    activeTime: null,
    activeDate: discountInput.startDate,
    endDate: discountInput.endDate,
    endTime: null,
    recurringCycleLimitAmount,
    offerCodePurchaseRestrictionsRule: discountInput.recurringCycleLimit === 1 ? RecurringCycleLimit.LIMIT_DISCOUNT_TO_THE_FIRST_PAYMENT : discountInput.recurringCycleLimit === 0 ? RecurringCycleLimit.DISCOUNT_APPLIES_TO_ALL_RECURRING_PAYMENTS : RecurringCycleLimit.LIMIT_DISCOUNT_TO_MULTIPLE_RECURRING_PAYMENTS,
    isEndDateEnable: false,
    isNewFlow: true,
    purchaseType,
    isMultipleShopifySyncEnabled: false,
    isSameDiscountMultipleShopify: true,
    multiShopifyEnabled: false,
    clientsForSync: [],
    connectedAdvertiserForSync: [],
    allClientForSync: [],
    isUngrouped: false,
    lockEditing: [],
    productDiscounts: false,
    shippingDiscounts: false,
    orderDiscounts: false,
    isPromoLink: false,
    isLandingPageEnabled: false,
    landingPageUrl: null,
    shopifyRedirectUrl: null,
    isSecureCodes: false,
    linkedShopifyOfferId: '',
    isReadOnly: false,
    offerType: PROMO_OFFER_TYPES.AMOUNT_OFF,
  };
};

export const processProductCollections = (productCollectionIds: number[], productCollections: ProductCollections[]) => {
  const productCollectionsForInput = map(productCollectionIds, (collectionId) => {
    const collections = find(productCollections, (collection) => collection.id === collectionId);
    return {
      title: collections.title,
      id: collections.id,
    };
  });
  return productCollectionsForInput;
};

export const transformDiscountToUpdatePromoInput = (
  values: Partial<IShopifyPromoCodeFormValues>,
  formOptionsData: IShopifyPromoCodeFormOptions,
  isSubscriptionEnable: boolean,
): UpdatePromoInput => {
  const {
    prefixType,
    codeSuffix,
    isOneTime,
    isSubscription,
    recurringCycleLimitAmount: recurringCycleLimit,
  } = processPromoCodeInput(values, isSubscriptionEnable);
  return {
    codeSuffix,
    flatPayout: 0,
    payouts: [],
    name: values.groupName,
    payoutType: values.payoutType,
    percentPayout: 0,
    prefixType,
    priceRuleAmount: Number(values.priceRuleAmount),
    priceRuleType: values.priceRuleType,
    source: OFFER_SOURCE.SHOPIFY,
    specialLimitProductCollections: !isEmpty(values?.productCollections) || values?.productCollections == null,
    productCollections: processProductCollections(
      values?.productCollections,
      formOptionsData?.productCollectionOptions,
    ),
    specialLimitNewCustomersOnly: !!values.specialLimitNewCustomersOnly,
    specialLimitOnePerSale: !!values.specialLimitOnePerSale,
    specialLimitUsageCapAmount: Number(values.specialLimitUsageCapAmount) || null,
    specialLimitUsageCapEnabled: !!values.specialLimitUsageCapEnabled,
    usageLimitAmount: Number(values.usageLimitAmount) || null,
    usageLimitRule: values.usageLimitRule,
    status: values.status,
    isUngrouped: values.isUngrouped,
    recurringCycleLimit,
    isSubscription,
    isOneTime,
    codePrefix: values.isPrefixSelected || values.codePrefix ? values.codePrefix : '',
    startDate: values.activeDate,
    endDate: values.endDate ? values.endDate : null,
    multiShopifyEnabled: false,
    clientsForSync: [],
    discountCombination: {
      productDiscounts: false,
      orderDiscounts: false,
      shippingDiscounts: false,
    },
    isSecureCodes: false,
  };
};

export function getEmptyBoostInput(boostType: BOOST_TYPE): BoostInput {
  return {
    label: '',
    boostType, // make this field nullable
    triggerType: '',
    triggerValue: 0,
    activeEndDate: null,
    activeStartDate: null,
    maxLimit: null,
    hideInCreatorPortal: false,
    requireApproval: false,
    boostEligibility: {
      eligibilityType: BoostEligibilitySelectionType.ALL,
      memberIds: null,
      id: null,
      advancedSelectionMetadata: null,
      advancedSelectionOperator: null,
    },
  };
}

export const getStatusForAffiliateLinkMember = (member: any): OfferMemberStatus => {
  const status = member.linkStatus?.trim().toUpperCase();
  switch (status) {
    case AFFILIATE_OFFER_LINK_STATUS.ACTIVE:
      return OfferMemberStatus.ACTIVE;
    case AFFILIATE_OFFER_LINK_STATUS.DISABLED:
      return OfferMemberStatus.PAUSED;
    case AFFILIATE_OFFER_LINK_STATUS.PENDING:
      return OfferMemberStatus.PENDING;
    case AFFILIATE_OFFER_LINK_STATUS.PENDING_UNPAUSE:
      return OfferMemberStatus.PENDING_UPDATE;
    case AFFILIATE_OFFER_LINK_STATUS.PENDING_PAUSE:
      return OfferMemberStatus.PENDING_DELETION;
    case AFFILIATE_OFFER_LINK_STATUS.FAILED_UPDATE:
      return OfferMemberStatus.FAILED_UPDATE;
    case AFFILIATE_OFFER_LINK_STATUS.DELETING:
      return OfferMemberStatus.DELETING;
  }
  return OfferMemberStatus.UNKNOWN;
};

export const getStatusForPromoCodeMember = (member: any): OfferMemberStatus => {
  switch (member.status) {
    case OFFER_PROMO_CODE_STATUS.PENDING:
      return OfferMemberStatus.PENDING;
    case OFFER_PROMO_CODE_STATUS.PENDING_DELETION:
      return OfferMemberStatus.PENDING_DELETION;
    case OFFER_PROMO_CODE_STATUS.PENDING_UPDATE:
      return OfferMemberStatus.PENDING_UPDATE;
    case OFFER_PROMO_CODE_STATUS.DELETED:
      return OfferMemberStatus.DEACTIVATED;
    case OFFER_PROMO_CODE_STATUS.FAILED_DUPLICATE:
    case OFFER_PROMO_CODE_STATUS.FAILED_OTHER:
    case OFFER_PROMO_CODE_STATUS.FAILED_TOO_LONG:
    case OFFER_PROMO_CODE_STATUS.FAILED_DELETION:
      return OfferMemberStatus.FAILED;
    case OFFER_PROMO_CODE_STATUS.EXPIRED:
      return OfferMemberStatus.EXPIRED;
    case OFFER_PROMO_CODE_STATUS.SUCCESS:
      return OfferMemberStatus.ACTIVE;
  }
  return OfferMemberStatus.UNKNOWN;
};

export const getMemberStatus = (memberStatus: AFFILIATE_STATUS): MemberStatus => {
  switch (memberStatus) {
    case AFFILIATE_STATUS.ACTIVE:
      return MemberStatus.ACTIVE;
    case AFFILIATE_STATUS.DELETED:
      return MemberStatus.DELETED;
  }
  return MemberStatus.ACTIVE;
};

export const computeOfferMembers = (offer: GetOfferById['offer']): TOfferMember[] => {
  if (!offer) {
    return [];
  }

  const promo = offer.promos?.[0];
  const link = offer.links?.[0];

  if (isEmpty(offer.promos) && isEmpty(offer.links)) {
    return [];
  }

  // Determine source
  const source = isEmpty(offer.promos) && !isEmpty(offer.links) ? OFFER_SOURCE.TUNE : OFFER_SOURCE.SHOPIFY;

  switch (source) {
    case OFFER_SOURCE.SHOPIFY:
      if (isEmpty(offer.promos)) {
        return [];
      }

      return map(promo.affiliateStats, (member) => {
        const promoMember = find(promo.affiliates, (affiliate) => affiliate.affiliateId === member.affiliateId);

        if (isUndefined(promoMember) || promoMember.deletedDate !== null) {
          return null;
        }

        // Handle promo link case
        const linkMemberStat = offer.isPromoLink
          ? find(
              first(offer.links)?.affiliateStats || [],
              (linkAffiliate) => linkAffiliate.affiliateId === member.affiliateId,
            )
          : null;

        const memberData: IPromoCodeMember = {
          affiliateId: member.affiliateId,
          affiliateOfferId: promoMember.id,
          avgSale: member.avgSale,
          code: member.affiliateCode,
          payoutId: promoMember.offerPayoutId,
          codeEndDate: promoMember.endDate,
          codeStartDate: promoMember.startDate,
          conversions: member.conversions,
          email: member.affiliateEmail,
          imageUrl: member.affiliateImageUrl,
          memberId: member.aspirexMemberId,
          name: member.affiliateName,
          payoutEarned: member.payoutEarned,
          payoutMade: member.payoutMade,
          sales: member.sales,
          salesBase: member.salesBase,
          avgSaleBase: member.avgSaleBase,
          payoutEarnedBase: member.payoutEarnedBase,
          currencies: promo.stats.baseCurrencies,
          source: OFFER_SOURCE.SHOPIFY,
          status: promoMember ? getStatusForPromoCodeMember(promoMember) : OfferMemberStatus.UNKNOWN,
          memberStatus: promoMember ? getMemberStatus(promoMember.affiliate.status) : MemberStatus.ACTIVE,
          promoCodeStatus: promoMember.status,
          externalDiscountCodeGid: promoMember.externalDiscountCodeGId,
          providerMetadata: promoMember.providerMetadata,
          createdDate: promoMember.createdDate,
          deletedDate: promoMember.deletedDate,
          offerAutomationConfigId: promoMember?.offerAutomationConfigId,
          boostDataForAffiliateOffer: promoMember?.boostDataForAffiliateOffer,
        };

        // Add promo link specific data
        if (offer.isPromoLink) {
          const linkAffiliate = find(
            first(offer.links)?.affiliates || [],
            (affiliate) => affiliate.affiliateId === member.affiliateId,
          );

          return {
            ...memberData,
            affiliateShortLink: linkAffiliate?.affiliateShortLink,
            affiliateLink: find(
              first(offer.links)?.affiliateStats || [],
              (affiliate) => affiliate.affiliateId === member.affiliateId,
            )?.affiliateLink,
            clicks: find(
              first(offer.links)?.affiliateStats || [],
              (affiliate) => affiliate.affiliateId === member.affiliateId,
            )?.clicks,
            conversions: member.conversions + (linkMemberStat?.nonDuplicateConversions || 0),
            payoutEarned: member.payoutEarned + (linkMemberStat?.payoutEarned || 0),
            payoutMade: member.payoutMade + (linkMemberStat?.payoutMade || 0),
            sales: member.sales + (linkMemberStat?.sales || 0),
            avgSale:
              (member.sales + (linkMemberStat?.sales || 0)) /
              (member.conversions + (linkMemberStat?.nonDuplicateConversions || 0)),
            avgSaleBase:
              member.conversions + (linkMemberStat?.nonDuplicateConversions || 0)
                ? (member.salesBase + (linkMemberStat?.salesBase || 0)) /
                  (member.conversions + (linkMemberStat?.nonDuplicateConversions || 0))
                : 0,
            salesBase: member.salesBase + (linkMemberStat?.salesBase || 0),
            payoutEarnedBase: member.payoutEarnedBase + (linkMemberStat?.payoutEarnedBase || 0),
          };
        }

        return memberData;
      }).filter(Boolean) as TOfferMember[];

    case OFFER_SOURCE.TUNE:
      if (isEmpty(offer.links)) {
        return [];
      }

      return map(
        link.affiliateStats,
        (member): TOfferMember | null => {
          const linkMember = find(link.affiliates, (affiliate) => affiliate.affiliateId === member.affiliateId);

          if (isUndefined(linkMember) || linkMember.deletedDate !== null) {
            return null;
          }

          return {
            affiliateId: linkMember.affiliateId,
            affiliateOfferId: linkMember.id,
            memberAffiliateOfferId: member.affiliateOfferId,
            affiliateLink: member.affiliateLink,
            affiliateShortLink: member.affiliateShortLink,
            avgSale: member.avgSale,
            payoutId: linkMember.offerPayoutId,
            clicks: member.clicks,
            conversions: member.conversions,
            email: member.affiliateEmail,
            imageUrl: member.affiliateImageUrl,
            linkCreationDate: member.linkCreationDate,
            memberId: member.aspirexMemberId,
            name: member.affiliateName,
            payoutEarned: member.payoutEarned,
            payoutMade: member.payoutMade,
            sales: member.sales,
            source: OFFER_SOURCE.TUNE,
            status: getStatusForAffiliateLinkMember(member),
            memberStatus: linkMember ? getMemberStatus(linkMember.affiliate.status) : MemberStatus.ACTIVE,
            linkStatusError: linkMember.updateLog,
            additionalDeepLinks: linkMember.affiliateDeeplinks,
            defaultDeeplink: linkMember.deeplinkUrl,
            deletedDate: linkMember.deletedDate,
            currencies: link.stats.baseCurrencies,
            avgSaleBase: member.avgSaleBase,
            salesBase: member.salesBase,
            payoutEarnedBase: member.payoutEarnedBase,
            offerAutomationConfigId: linkMember?.offerAutomationConfigId,
          } as IAffiliateLinkMember;
        },
      ).filter(Boolean) as TOfferMember[];

    default:
      return [];
  }
};
